import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { reportsService } from "../../../api";
import { Table, Button, BackArrow } from "../../../components";
import { useUi } from "../../../context/ui.context";
import { usePageTitle } from "../../../context/navigationContext";

interface MonthlyReport {
  month_display: string;
  revenue: number;
  add_credit: number;
  subtract_credit: number;
  taxes: number;
  currency: string;
}

const YearlyRevenue: React.FC = () => {
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(t("YEARLY_REVENUE_TITLE"));
  const [report, setReport] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    loadReport(selectedYear);
  }, []);

  const loadReport = async (year: number) => {
    try {
      setLoading(true);
      const response = await reportsService.getYearlyRevenueReport(year);
      setReport(response.data.report);
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_LOAD_REPORT"),
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  };

  const navigateLeft = () => {
    const newYear = selectedYear - 1;
    setSelectedYear(newYear);
    loadReport(newYear);
  };

  const navigateRight = () => {
    const newYear = selectedYear + 1;
    setSelectedYear(newYear);
    loadReport(newYear);
  };

  const exportToCSV = () => {
    if (!report || report.length === 0) return;

    const table = document.getElementById("report-table");
    if (!table) return;

    const rows = table.querySelectorAll("tr");
    const data: string[] = [];

    rows.forEach((row) => {
      const cols = row.querySelectorAll("td, th");
      const rowData: string[] = [];
      cols.forEach((col) => {
        rowData.push(`"${col.textContent || ""}"`);
      });
      data.push(rowData.join(","));
    });

    const csv = data.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.download = `yearly_revenue_report_${selectedYear}.csv`;
    link.href = window.URL.createObjectURL(blob);
    link.click();
  };

  const tableRows = report.map((monthlyReport, index) => ({
    id: index,
    month_display: monthlyReport.month_display,
    revenue: formatCurrency(monthlyReport.revenue, monthlyReport.currency),
    add_credit: formatCurrency(monthlyReport.add_credit, monthlyReport.currency),
    subtract_credit: formatCurrency(monthlyReport.subtract_credit, monthlyReport.currency),
    taxes: formatCurrency(monthlyReport.taxes, monthlyReport.currency),
  }));

  const columns = [
    { title: t("MONTH"), key: "month_display" },
    { title: t("REVENUE"), key: "revenue" },
    { title: t("ADD_CREDIT"), key: "add_credit" },
    { title: t("SUBTRACT_CREDIT"), key: "subtract_credit" },
    { title: t("TAXES"), key: "taxes" },
  ];

  return (
    <Container>
      <BackArrow fallbackPath="/utilities/reports" />
      <Title>{t("YEARLY_REVENUE_TITLE")}</Title>
      <Description>{t("YEARLY_REVENUE_INFO")}</Description>

      {!loading && (
        <NavigationContainer>
          <NavButton onClick={navigateLeft}>
            ← {selectedYear - 1}
          </NavButton>
          <CurrentYear>{selectedYear}</CurrentYear>
          {currentYear !== selectedYear && (
            <NavButton onClick={navigateRight}>
              {selectedYear + 1} →
            </NavButton>
          )}
        </NavigationContainer>
      )}

      {loading ? (
        <>
          <Skeleton height={50} style={{ marginBottom: 20 }} />
          <Skeleton height={400} />
        </>
      ) : (
        report &&
        report.length > 0 && (
          <>
            <TableWrapper id="report-table">
              <Table
                columns={columns}
                rows={tableRows}
                hideCheckbox
                hideOther
              />
            </TableWrapper>

            <ButtonContainer>
              <Button
                variant="stroke"
                size="display"
                title={t("EXPORT_TO_CSV")}
                onClick={exportToCSV}
              />
            </ButtonContainer>
          </>
        )
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
  margin-bottom: 16px;
  margin-top: 16px;
`;

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.palette[700]};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
`;

const NavButton = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.palette[10]};
  border: 1px solid ${({ theme }) => theme.palette[30]};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette[900]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.palette[20]};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CurrentYear = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
`;

const TableWrapper = styled.div`
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

export default YearlyRevenue;
